import React, { useEffect, useState } from "react";

import { useMediaQuery } from "@mui/material";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";

import { fonts, fontSizes } from "@/styles/design-system";

/**
 * In-page alert banner on /claims that invites users with detected
 * music-use claims to apply to the Music Creator Program.
 *
 * Visibility model:
 *   - Polls GET /claims/my/music-use-info to learn { hasNewDetection, count }.
 *   - Hidden whenever hasNewDetection is false (none yet, all resolved,
 *     dismissed without a NEW detection since, or user is on the
 *     music_creator plan — backend suppresses there).
 *   - Single banner regardless of count. Plural/singular copy switches
 *     on `count`.
 *   - Clicking X dismisses (POST /claims/my/dismiss-music-use-banner)
 *     + plays the fade-out animation locally before the next poll
 *     would also hide it. Banner re-appears as soon as a NEW music_use
 *     detection lands.
 *
 * Visual style mirrors the in-page SubStateBanners pattern on this same
 * page — orange `#c28013`, rounded, max-height + opacity fade
 * (360ms cubic-bezier), X close in the top-right.
 */
const MusicUseInviteBanner: React.FC = () => {
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const evaluate = async () => {
      try {
        const resp = await instance.get("/claims/my/music-use-info");
        const data: { hasNewDetection: boolean; count: number } = resp?.data ?? { hasNewDetection: false, count: 0 };
        if (cancelled) return;
        setCount(data.count);
        if (data.hasNewDetection) {
          setFadingOut(false);
          setShow(true);
        } else {
          setShow(false);
        }
      } catch {
        if (!cancelled) setShow(false);
      }
    };
    void evaluate();
    const onFocus = () => { void evaluate(); };
    window.addEventListener("focus", onFocus);
    const pollId = window.setInterval(() => { void evaluate(); }, 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(pollId);
    };
  }, []);

  const handleApply = () => {
    window.location.assign("/music-creator");
  };

  const handleDismiss = async () => {
    setFadingOut(true);
    try {
      await instance.post("/claims/my/dismiss-music-use-banner");
    } catch {
      /* swallow — even if the persist fails the local fade still hides
         the banner; next poll will re-evaluate from server truth. */
    }
    window.setTimeout(() => setShow(false), 380);
  };

  if (!show) return null;

  const sg = count <= 1;
  const message = sg
    ? "1 video uses our music as the main focus, not just background. Apply to the Music Creator Program to resolve."
    : `${count} videos use our music as the main focus, not just background. Apply to the Music Creator Program to resolve.`;

  return (
    <div
      data-banner-surface
      style={{
      position: "relative",
      display: "flex",
      // Phone/tablet: stack the message over the button so the text gets the
      // full width instead of being squeezed into a narrow column beside it.
      flexDirection: isCompact ? "column" : "row",
      alignItems: isCompact ? "flex-start" : "center",
      justifyContent: "flex-start",
      gap: isCompact ? 12 : 16,
      // 12/16 is what the channel-warning banners above use. The right side
      // stays wide because the dismiss button floats over it.
      padding: fadingOut ? "0 48px 0 16px" : (isCompact ? "12px 44px 12px 16px" : "12px 48px 12px 16px"),
      marginBottom: fadingOut ? 0 : 16,
      maxHeight: fadingOut ? 0 : 240,
      opacity: fadingOut ? 0 : 1,
      borderRadius: 12,
      ...bannerSurface("rgb(195, 75, 54)"), border: "none",
      overflow: "hidden",
      transition: "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 360ms cubic-bezier(0.4, 0, 0.2, 1)",
    }}>
      <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: "#fff" }}>
        {message}
      </div>
      <button
        onClick={handleApply}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 16px",
          borderRadius: 8,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          fontFamily: fonts.body,
          fontSize: fontSizes.sm,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 16px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(186, 144, 92, 0.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 16px rgba(186, 144, 92, 0.25)"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
        </svg>
        Apply Now
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          // Aligned on the DRAWN cross, not the box around it: the warning
          // banners use a text glyph that nearly fills its box, while this is a
          // 22px SVG whose strokes sit ~5px inside their own viewBox. Matching
          // the boxes left the visible cross further left than theirs.
          top: isCompact ? 12 : "50%", right: 10,
          transform: isCompact ? "none" : "translateY(-50%)",
          // 32/22 is the size every other banner's dismiss uses.
          width: 32, height: 32, borderRadius: "50%",
          background: "transparent", border: "none",
          color: "rgba(255, 255, 255, 0.98)", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255, 255, 255, 0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default MusicUseInviteBanner;
